import { Button } from "@/components/ui/Button";

type ConfigNode = {
  type?: string;
  event?: string;
  props?: Record<string, unknown>;
  bind?: { value?: string };
  children?: ConfigNode[];
};

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value == null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function interpolateState(
  template: unknown,
  state: Record<string, unknown>,
): string {
  const text = asText(template);
  return text.replace(/\{\{\s*state\.([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) =>
    asText(state[key]),
  );
}

/** Pull OpenUI-ish configuration.children from a Studio preview tree. */
export function extractMiniAppConfigChildren(tree: unknown): ConfigNode[] {
  if (!tree || typeof tree !== "object") return [];
  const queue: unknown[] = [tree];
  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== "object") continue;
    const node = current as {
      configuration?: { children?: unknown };
      children?: unknown[];
    };
    const configured = node.configuration?.children;
    if (Array.isArray(configured) && configured.length > 0) {
      return configured.filter(
        (item): item is ConfigNode =>
          Boolean(item) && typeof item === "object",
      );
    }
    if (Array.isArray(node.children)) {
      queue.push(...node.children);
    }
  }
  return [];
}

type StudioMiniAppConfigRendererProps = {
  nodes: ConfigNode[];
  state: Record<string, unknown>;
  urlValue: string;
  onUrlChange: (value: string) => void;
  onEvent: (eventId: string, payload?: Record<string, unknown>) => void;
  disabled?: boolean;
};

/** Renders draft MiniApp OpenUI configuration nodes when the program stub is empty. */
export default function StudioMiniAppConfigRenderer({
  nodes,
  state,
  urlValue,
  onUrlChange,
  onEvent,
  disabled = false,
}: StudioMiniAppConfigRendererProps) {
  if (!nodes.length) return null;

  return (
    <div
      className="space-y-3 rounded-xl border border-border bg-background p-4"
      data-testid="studio-miniapp-config-renderer"
    >
      {nodes.map((node, index) => {
        const type = String(node.type ?? "").toLowerCase();
        const props = node.props ?? {};
        const key = `${type}:${index}`;

        if (type === "textblock" || type === "text") {
          return (
            <p key={key} className="text-sm text-foreground whitespace-pre-wrap">
              {interpolateState(props.text ?? props.value, state)}
            </p>
          );
        }

        if (type === "textfield" || type === "input") {
          const bindPath = String(node.bind?.value ?? "");
          const isUrl =
            bindPath === "state.url" ||
            String(props.label ?? "").toLowerCase() === "url";
          const value = isUrl
            ? urlValue
            : interpolateState(props.value, state);
          return (
            <label key={key} className="block space-y-1.5 text-sm">
              <span className="font-medium text-foreground">
                {asText(props.label) || "Champ"}
              </span>
              <input
                type={isUrl ? "url" : "text"}
                value={value}
                disabled={disabled || !isUrl}
                placeholder={asText(props.placeholder)}
                onChange={(event) => {
                  if (isUrl) onUrlChange(event.target.value);
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground disabled:opacity-60"
              />
            </label>
          );
        }

        if (type === "button") {
          const eventId = String(node.event ?? "").trim();
          return (
            <Button
              key={key}
              type="button"
              size="sm"
              disabled={disabled || !eventId}
              onClick={() =>
                onEvent(eventId, {
                  url: urlValue,
                })
              }
            >
              {asText(props.label) || eventId || "Action"}
            </Button>
          );
        }

        return (
          <pre
            key={key}
            className="overflow-auto rounded-md bg-muted/40 p-2 text-[11px] text-muted-foreground"
          >
            {JSON.stringify(node, null, 2)}
          </pre>
        );
      })}
    </div>
  );
}
